
/**
 * Standard error codes used across frontend and backend
 * Each error has a unique UUID and user-friendly message
 */
export interface ErrorCode {
  id: string;
  message: string;
  description?: string;
}

export const ERROR_CODES = {
  // General errors
  GENERAL_ERROR: {
    id: "e0cb3a1d-ee4e-4af9-9d3b-dd8137c1f9c3",
    message: "Something went wrong",
    description: "An unexpected error occurred. Please try again later."
  },
  
  // Authentication errors
  AUTH_REQUIRED: {
    id: "3b7c9d4e-5a12-47f8-b3e1-9c8d6f7b2a3d",
    message: "Authentication required",
    description: "You must be logged in to perform this action."
  },
  
  // Website analysis errors
  WEBSITE_ANALYSIS_COOLDOWN: {
    id: "b2c7d8e9-f0a1-42b3-83c4-95d6e7f8a9b0",
    message: "Analysis cooldown period",
    description: "Website analysis can only be performed once every 24 hours."
  },
  WEBSITE_ANALYSIS_FAILED: {
    id: "7a8b9c0d-1e2f-43a4-b5c6-d7e8f9a0b1c2",
    message: "Analysis failed",
    description: "We couldn't analyze this website. Please check the URL and try again."
  },
  WEBSITE_URL_REQUIRED: {
    id: "d4e5f6a7-b8c9-40d1-a2b3-c4d5e6f7a8b9",
    message: "Website URL required",
    description: "Please enter a website URL to analyze."
  },
  WEBSITE_URL_INVALID: {
    id: "f8e9d0c1-b2a3-45c6-d7e8-f9g0h1i2j3k4",
    message: "Invalid website URL",
    description: "Please enter a valid website URL."
  },
  
  // File upload errors
  FILE_UPLOAD_FAILED: {
    id: "a1b2c3d4-e5f6-47a7-b8c9-d0e1f2a3b4c5",
    message: "File upload failed",
    description: "We couldn't upload your file. Please try again."
  },
  FILE_TYPE_NOT_SUPPORTED: {
    id: "f1e2d3c4-b5a6-47a7-8b9c-0d1e2f3a4b5",
    message: "File type not supported",
    description: "This file type is not supported. Please upload a PDF or DOCX file."
  },
  FILE_TOO_LARGE: {
    id: "c1d2e3f4-a5b6-47c7-d8e9-f0a1b2c3d4e5",
    message: "File too large",
    description: "The file is too large. Please upload a file smaller than 5MB."
  },
  
  // Supabase storage errors
  STORAGE_PERMISSION_DENIED: {
    id: "d5e6f7g8-h9i0-4j1k-l2m3-n4o5p6q7r8s9",
    message: "Storage permission denied",
    description: "You don't have permission to access this file."
  },
  
  // Business profile errors
  BUSINESS_INFO_MISSING: {
    id: "t1u2v3w4-x5y6-4z7a-b8c9-d0e1f2g3h4i5",
    message: "Missing business information",
    description: "Please provide all required business information."
  },
  
  // API errors
  API_CONNECTION_ERROR: {
    id: "j5k6l7m8-n9o0-4p1q-r2s3-t4u5v6w7x8y9",
    message: "API connection error",
    description: "Could not connect to the service. Please check your internet connection and try again."
  },
  API_TIMEOUT_ERROR: {
    id: "z1a2b3c4-d5e6-4f7g-h8i9-j0k1l2m3n4o5",
    message: "API timeout",
    description: "The service took too long to respond. Please try again later."
  }
};

/**
 * Get an error code by its ID
 */
export function getErrorById(id: string): ErrorCode | undefined {
  return Object.values(ERROR_CODES).find(error => error.id === id);
}

/**
 * Check if a website analysis is on cooldown
 */
export function isWebsiteAnalysisOnCooldown(lastAnalyzedAt?: number): boolean {
  if (!lastAnalyzedAt) return false;
  
  const now = Date.now();
  const timeSinceLastAnalysis = now - lastAnalyzedAt;
  const analysisCooldown = 86400000; // 24 hours in milliseconds
  
  return timeSinceLastAnalysis < analysisCooldown;
}

/**
 * Get hours remaining until website analysis cooldown expires
 */
export function getCooldownHoursRemaining(lastAnalyzedAt?: number): number {
  if (!lastAnalyzedAt) return 0;
  
  const now = Date.now();
  const timeSinceLastAnalysis = now - lastAnalyzedAt;
  const analysisCooldown = 86400000; // 24 hours in milliseconds
  
  return Math.ceil((analysisCooldown - timeSinceLastAnalysis) / 3600000);
}

/**
 * Map any error to our standard error codes
 */
export function normalizeError(error: unknown): ErrorCode {
  if (!error) {
    return ERROR_CODES.GENERAL_ERROR;
  }
  
  // If error is already one of our error codes (by ID)
  if (typeof error === 'string') {
    const knownError = getErrorById(error);
    if (knownError) return knownError;
  }
  
  // If error is an Error object with a message
  if (error instanceof Error) {
    // Try to find a known error with matching message pattern
    const knownErrorFromMessage = Object.values(ERROR_CODES).find(
      code => error.message.includes(code.message)
    );
    
    if (knownErrorFromMessage) return knownErrorFromMessage;
    
    // Map common error patterns to our error codes
    if (error.message.includes('permission denied')) {
      return ERROR_CODES.STORAGE_PERMISSION_DENIED;
    }
    
    if (error.message.includes('authentication')) {
      return ERROR_CODES.AUTH_REQUIRED;
    }
    
    if (error.message.includes('timeout') || error.message.includes('timed out')) {
      return ERROR_CODES.API_TIMEOUT_ERROR;
    }
    
    if (error.message.includes('network') || error.message.includes('connection')) {
      return ERROR_CODES.API_CONNECTION_ERROR;
    }
    
    // Create a custom error with the original message
    return {
      id: ERROR_CODES.GENERAL_ERROR.id,
      message: ERROR_CODES.GENERAL_ERROR.message,
      description: error.message
    };
  }
  
  // Default to general error
  return ERROR_CODES.GENERAL_ERROR;
}
